import Firebase from 'firebase/compat/app'
import 'firebase/compat/auth'

export const MULTI_FACTOR_REQUIRED_CODE = 'auth/multi-factor-auth-required'
export const REQUIRES_RECENT_LOGIN_CODE = 'auth/requires-recent-login'
export const PHONE_FACTOR_ID = Firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID

const E164_PHONE_PATTERN = /^\+[1-9]\d{7,14}$/

type FirebaseErrorLike = {
  code?: string
  message?: string
}

export const normalizePhoneNumber = (value: string) =>
  value
    .trim()
    .replace(/[\s().-]/g, '')
    .replace(/^00/, '+')

export const isPhoneNumberValid = (value: string) =>
  E164_PHONE_PATTERN.test(normalizePhoneNumber(value))

export const isPhoneMultiFactorInfo = (
  info: Firebase.auth.MultiFactorInfo
): info is Firebase.auth.PhoneMultiFactorInfo =>
  info.factorId === PHONE_FACTOR_ID

export const isMultiFactorError = (
  error: unknown
): error is Firebase.auth.MultiFactorError =>
  typeof error === 'object' &&
  error != null &&
  (error as FirebaseErrorLike).code === MULTI_FACTOR_REQUIRED_CODE &&
  'resolver' in error

export const getMaskedPhoneNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) {
    return 'saved phone number'
  }

  const trimmed = phoneNumber.trim()
  const lastFour = trimmed.replace(/\D/g, '').slice(-4)

  if (!lastFour) {
    return trimmed
  }

  return `***-***-${lastFour}`
}

export const getMultiFactorLabel = (info: Firebase.auth.MultiFactorInfo) => {
  const displayName = info.displayName?.trim()

  if (displayName) {
    return displayName
  }

  if (isPhoneMultiFactorInfo(info)) {
    return `Text message to ${getMaskedPhoneNumber(info.phoneNumber)}`
  }

  return 'Security key'
}

export const getFriendlyFirebaseErrorMessage = (
  error: unknown,
  fallbackMessage: string
) => {
  const code = (error as FirebaseErrorLike | null)?.code
  const message = (error as FirebaseErrorLike | null)?.message

  switch (code) {
    case 'auth/invalid-verification-code':
      return 'The verification code is invalid.'
    case 'auth/missing-verification-code':
      return 'Enter the verification code that was sent to your phone.'
    case 'auth/code-expired':
      return 'The verification code expired. Request a new code and try again.'
    case 'auth/invalid-verification-id':
      return 'The verification request expired. Request a new code and try again.'
    case 'auth/too-many-requests':
      return 'Too many attempts were made. Wait a few minutes and try again.'
    case 'auth/invalid-phone-number':
      return 'Use a mobile number in international format, for example +15551234567.'
    case 'auth/quota-exceeded':
      return 'The Firebase SMS quota is currently exhausted. Try again later.'
    case 'auth/unverified-email':
      return 'Verify your email address before enabling two-step verification.'
    case 'auth/second-factor-already-in-use':
      return 'That phone number is already enrolled as a second factor.'
    case 'auth/maximum-second-factor-count-exceeded':
      return 'This account already has the maximum number of second factors.'
    case REQUIRES_RECENT_LOGIN_CODE:
      return 'For security, sign out and sign back in before changing two-step verification.'
    case MULTI_FACTOR_REQUIRED_CODE:
      return 'A second verification step is required to finish signing in.'
    default:
      return message?.trim() || fallbackMessage
  }
}
